import React, { useState, useEffect } from 'react';

interface AddVehicleModalProps {
  open: boolean;
  onAdd: (vehicle: any, files: any) => void;
  onClose: () => void;
  typeOfCarDefault?: string;
  contractTypeDefault?: string;
}

const AddVehicleModal: React.FC<AddVehicleModalProps> = ({ 
  open, 
  onAdd, 
  onClose, 
  typeOfCarDefault, 
  contractTypeDefault 
}) => {
  const [form, setForm] = useState({
    model: '',
    manufacturer: '',
    license_plate: '',
    vin: '',
    type_of_car: '',
    km: '',
    type_of_contract: '',
    contract_end_date: '',
    tuev_until: '',
    monthly_net_sum: '',
    monthly_gross_sum: '',
    poolcar: false,
    date_of_last_km_check: '',
    taxation: 'Keine Versteuerung',
  });

  const [files, setFiles] = useState({
    angebote: [],
    vertrage: [],
    versicherung: [],
    tuev: [],
    sonstiges: [],
  });

  useEffect(() => {
    setForm({
      model: '',
      manufacturer: '',
      license_plate: '',
      vin: '',
      type_of_car: typeOfCarDefault || '',
      km: '',
      type_of_contract: contractTypeDefault || '',
      contract_end_date: '',
      tuev_until: '',
      monthly_net_sum: '',
      monthly_gross_sum: '',
      poolcar: false,
      date_of_last_km_check: '',
      taxation: 'Keine Versteuerung',
    });
  }, [open, typeOfCarDefault, contractTypeDefault]);

  if (!open) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    let fieldValue: string | boolean = value;
    if (type === 'checkbox') {
      fieldValue = (e.target as HTMLInputElement).checked;
    }
    setForm((prev) => ({
      ...prev,
      [name]: fieldValue,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, category: string) => {
    const fileList = e.target.files;
    if (fileList && fileList.length > 0) {
      setFiles((prev: any) => ({
        ...prev,
        [category]: Array.from(fileList),
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newVehicle = { ...form };
    if (form.km && form.km !== '') {
      newVehicle.date_of_last_km_check = new Date().toISOString().split('T')[0];
    }

    onAdd(newVehicle, files);
    setForm({
      model: '',
      manufacturer: '',
      license_plate: '',
      vin: '',
      type_of_car: '',
      km: '',
      type_of_contract: '',
      contract_end_date: '',
      tuev_until: '',
      monthly_net_sum: '',
      monthly_gross_sum: '',
      poolcar: false,
      date_of_last_km_check: '',
      taxation: 'Keine Versteuerung',
    });
    setFiles({ angebote: [], vertrage: [], versicherung: [], tuev: [], sonstiges: [] });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-12 w-full max-w-2xl relative animate-fade-in max-h-[80vh] overflow-y-auto">
        <button type="button" onClick={onClose} className="absolute top-6 right-6 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-3xl">&times;</button>
        <h3 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-8 text-center">Neues Fahrzeug anlegen</h3>
        
        <div className="grid grid-cols-2 gap-6 mb-8">
          <input 
            className="border rounded p-3 mb-2 w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white" 
            name="model" 
            value={form.model} 
            onChange={handleChange} 
            placeholder="Modell" 
            required 
          />
          <input 
            className="border rounded p-3 mb-2 w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white" 
            name="manufacturer" 
            value={form.manufacturer} 
            onChange={handleChange} 
            placeholder="Hersteller" 
          />
          <input 
            className="border rounded p-3 mb-2 w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white" 
            name="license_plate" 
            value={form.license_plate} 
            onChange={handleChange} 
            placeholder="Kennzeichen" 
          />
          <input 
            className="border rounded p-3 mb-2 w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white" 
            name="vin" 
            value={form.vin} 
            onChange={handleChange} 
            placeholder="FIN" 
          />
          <select
            name="type_of_car"
            value={form.type_of_car}
            onChange={handleChange}
            className="border rounded p-3 mb-2 w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option value="">Fahrzeugtyp auswählen</option>
            <option value="PKW">PKW</option>
            <option value="LKW">LKW</option>
            <option value="Transporter">Transporter</option>
            <option value="Motorrad">Motorrad</option>
            <option value="Anhänger">Anhänger</option>
            <option value="Sonstiges">Sonstiges</option>
          </select>
          <input 
            className="border rounded p-3 mb-2 w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white" 
            name="km" 
            value={form.km} 
            onChange={handleChange} 
            placeholder="Kilometerstand" 
            type="number" 
            min="0" 
          />
          <select
            name="type_of_contract"
            value={form.type_of_contract}
            onChange={handleChange}
            className="border rounded p-3 mb-2 w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option value="">Vertragstyp auswählen</option>
            <option value="Leasing">Leasing</option>
            <option value="Kauf">Kauf</option>
            <option value="Miete">Miete</option>
            <option value="Finanzierung">Finanzierung</option>
            <option value="Sonstiges">Sonstiges</option>
          </select>
          <input 
            className="border rounded p-3 mb-2 w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white" 
            name="contract_end_date" 
            value={form.contract_end_date} 
            onChange={handleChange} 
            placeholder="Vertragsende" 
            type="date" 
          />
          <input 
            className="border rounded p-3 mb-2 w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white" 
            name="tuev_until" 
            value={form.tuev_until} 
            onChange={handleChange} 
            placeholder="TÜV bis" 
            type="date" 
          />
          <input 
            className="border rounded p-3 mb-2 w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white" 
            name="monthly_net_sum" 
            value={form.monthly_net_sum} 
            onChange={handleChange} 
            placeholder="Monatlich Netto (€)" 
            type="number" 
            step="0.01" 
          />
          <input 
            className="border rounded p-3 mb-2 w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white" 
            name="monthly_gross_sum" 
            value={form.monthly_gross_sum} 
            onChange={handleChange} 
            placeholder="Monatlich Brutto (€)" 
            type="number" 
            step="0.01" 
          />
          <label className="flex items-center gap-2 col-span-2 mb-2">
            <input 
              type="checkbox" 
              name="poolcar" 
              checked={form.poolcar} 
              onChange={handleChange} 
            />
            <span className="dark:text-white">Poolcar</span>
          </label>
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-2 dark:text-white">Versteuerung</label>
            <select
              name="taxation"
              value={form.taxation}
              onChange={handleChange}
              className="border rounded p-3 mb-2 w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="Keine Versteuerung">Keine Versteuerung</option>
              <option value="1% Verbrenner">1% Verbrenner</option>
              <option value="0,5% Hybrid">0,5% Hybrid</option>
              <option value="0,25% Elektro">0,25% Elektro</option>
            </select>
          </div>
        </div>

        <div className="mb-8">
          <h4 className="text-lg font-semibold mb-2 dark:text-white">Dokumente direkt hochladen</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 dark:text-white">Angebote</label>
              <input 
                type="file" 
                multiple 
                onChange={e => handleFileChange(e, 'angebote')} 
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" 
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 dark:text-white">Verträge</label>
              <input 
                type="file" 
                multiple 
                onChange={e => handleFileChange(e, 'vertrage')} 
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" 
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 dark:text-white">Versicherung</label>
              <input 
                type="file" 
                multiple 
                onChange={e => handleFileChange(e, 'versicherung')} 
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" 
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 dark:text-white">TÜV</label>
              <input 
                type="file" 
                multiple 
                onChange={e => handleFileChange(e, 'tuev')} 
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" 
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1 dark:text-white">Sonstiges</label>
              <input 
                type="file" 
                multiple 
                onChange={e => handleFileChange(e, 'sonstiges')} 
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" 
              />
            </div>
          </div>
        </div>

        <button 
          type="submit" 
          className="w-full py-3 px-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-semibold text-xl mt-2"
        >
          Fahrzeug anlegen
        </button>
      </form>
    </div>
  );
};

export default AddVehicleModal; 